import { AsyncPipe } from '@angular/common';
import type { OnInit, Signal } from '@angular/core';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  input,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import type {
  OnyxOption,
  OnyxPointOfInterestAddress,
  OnyxSuggestion,
  OnyxTime,
  OnyxTimeRange,
} from '@onyx/angular';
import {
  ActionHelper,
  OnyxAddressInputComponent,
  OnyxAddressType,
  OnyxDropdownComponent,
  OnyxFormGroupComponent,
  OnyxIconBoxComponent,
  OnyxIconComponent,
  OnyxMessageComponent,
  OnyxSuggestionsComponent,
  OnyxTimepickerComponent,
  OnyxTimePipe,
} from '@onyx/angular';
import { chain } from 'lodash';
import { DateTime } from 'luxon';
import type { Observable } from 'rxjs';
import {
  combineLatest,
  combineLatestWith,
  distinctUntilChanged,
  filter,
  map,
  of,
  shareReplay,
  skip,
  startWith,
  switchMap,
} from 'rxjs';
import { DictionaryCode } from '../../../../../../common/enums/dictionary-code';
import { CommonHelper } from '../../../../../../common/helpers/common.helper';
import { SuggestionHelper } from '../../../../../../common/helpers/suggestions.helper';
import { BusinessHoursPipe } from '../../../../../../common/pipes/business-hours.pipe';
import { DictionariesService } from '../../../../../../common/services/dictionaries.service';
import { PointsOfInterestQueryParamKey } from '../../../../../management-panel/points-of-interest/common/enums/points-of-interest-query-param-key';
import { PointsOfInterestRoute } from '../../../../../management-panel/points-of-interest/common/enums/points-of-interest-route';
import type { PointOfInterest } from '../../../../../management-panel/points-of-interest/common/interfaces/point-of-interest';
import { PointsOfInterestService } from '../../../../../management-panel/points-of-interest/common/services/points-of-interest.service';
import { OrderFormRestorePointType } from '../../../../common/enums/order-form-cancel-action-type';
import { OrderPointCategory } from '../../../../common/enums/order-point-category';
import { OrderTimeWindowType } from '../../../../common/enums/order-time-window-type';
import type { OrderLoadingPointFormGroup } from '../../../../common/helpers/order-form.helper';
import { OrderFormService } from '../../../../common/services/order-form.service';
import { orderPointTimeWindowOverlap } from '../../../../common/validators/order-point-time-window-overlap.validator';
import type { OrderUnloadingPointFormGroup } from '../../order-unloading-point-form/order-unloading-point-form.component';
import { OrderPointTimeWindowFormComponent } from './order-point-time-window-form/order-point-time-window-form.component';
import { PointOfInterestHelper } from '../../../../../management-panel/points-of-interest/common/helpers/point-of-interest.helper';

type TimeWindowsFormGroup = ReturnType<
  typeof OrderPointBaseFormComponent.buildTimeWindowsForm
>;
export type TimeWindowsForm = ReturnType<TimeWindowsFormGroup['getRawValue']>;

@Component({
  selector: 'app-order-point-base-form',
  imports: [
    ReactiveFormsModule,
    OnyxIconBoxComponent,
    OnyxIconComponent,
    OnyxAddressInputComponent,
    OnyxDropdownComponent,
    OnyxSuggestionsComponent,
    OnyxFormGroupComponent,
    OnyxTimepickerComponent,
    AsyncPipe,
    OrderPointTimeWindowFormComponent,
    OnyxMessageComponent,
    BusinessHoursPipe,
    OnyxTimePipe,
    TranslatePipe,
  ],
  templateUrl: './order-point-base-form.component.html',
  styleUrl: './order-point-base-form.component.scss',
})
export class OrderPointBaseFormComponent implements OnInit {
  private readonly dictionariesService = inject(DictionariesService);
  private readonly injector = inject(Injector);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly orderFormService = inject(OrderFormService);
  private readonly router = inject(Router);
  private readonly pointOfInterestService = inject(PointsOfInterestService);
  private readonly destroyRef = inject(DestroyRef);

  public readonly form = input.required<
    OrderLoadingPointFormGroup | OrderUnloadingPointFormGroup
  >();
  public readonly isCompleted = input.required<boolean>();
  public readonly disabled = input.required<boolean>();

  protected readonly I18N = 'orders.orderForm.points';

  protected readonly OnyxAddressType = OnyxAddressType;
  protected readonly OrderTimeWindowType = OrderTimeWindowType;

  protected timeWindowTypes$!: Observable<OnyxOption<OrderTimeWindowType>[]>;
  protected timeWindowTypesSuggestions$!: Observable<
    OnyxSuggestion<OrderTimeWindowType>[]
  >;

  protected addressType!: Signal<OnyxAddressType | null>;
  protected timeWindowType!: Signal<OrderTimeWindowType>;

  protected sender = toSignal(this.orderFormService.sender$, {
    initialValue: null,
    injector: this.injector,
  });

  protected pointCategory = computed(() => this.form().controls.category.value);
  protected addressSuggestions = computed(() =>
    SuggestionHelper.getContractorPointOfInterestAddressSuggestions(
      this.sender()?.pointsOfInterest ?? [],
    ),
  );
  protected addressEmptyOptions = computed(
    (): OnyxOption<OnyxPointOfInterestAddress>[] =>
      (this.sender()?.pointsOfInterest ?? [])
        .slice(0, 7)
        .map(({ pointOfInterest }) => ({
          name: pointOfInterest.address.label,
          value: PointOfInterestHelper.getAddress(pointOfInterest),
        })),
  );
  protected serviceTimeLabel = computed(
    () => `forms.serviceTime.${this.pointCategory()}`,
  );
  protected timeWindowTypeLabel = computed(
    () => `${this.I18N}.term.${this.pointCategory()}`,
  );
  protected timeWindowTimeLabel = computed(
    () =>
      ({
        [OrderTimeWindowType.INTERVAL]: 'labels.interval',
        [OrderTimeWindowType.FIX]: 'Fix',
        [OrderTimeWindowType.OPENING_HOURS]: 'labels.openingHours',
      })[this.timeWindowType()],
  );
  protected timeWindowsFormArray = computed(
    () => this.form().controls.timeWindows.controls.windows,
  );

  protected pointOfInterest!: Signal<PointOfInterest | null>;
  protected serviceTime!: Signal<number | null>;
  protected showAfterHoursWarning!: Signal<false | OnyxTimeRange | OnyxTime>;
  protected showPastDateWarning!: Signal<boolean>;

  public ngOnInit(): void {
    const controls = this.form().controls;
    const timeWindowTypeControl = controls.timeWindows.controls.type;
    this.timeWindowType = toSignal(timeWindowTypeControl.valueChanges, {
      initialValue: timeWindowTypeControl.value,
      injector: this.injector,
    });

    const addressType$ = controls.address.valueChanges.pipe(
      startWith(controls.address.getRawValue()),
      map((address) => address?.type ?? null),
      distinctUntilChanged(),
    );
    this.addressType = toSignal(addressType$, {
      initialValue: controls.address.value?.type ?? null,
      injector: this.injector,
    });

    const timeWindowTypes$ = this.dictionariesService.getDictionary(
      DictionaryCode.ORDER_TIME_WINDOW_TYPE,
    );
    this.timeWindowTypes$ = combineLatest([
      timeWindowTypes$,
      addressType$,
    ]).pipe(
      map(([types, addressType]) =>
        addressType === OnyxAddressType.CUSTOM
          ? types.filter(
              ({ value }) => value !== OrderTimeWindowType.OPENING_HOURS,
            )
          : types,
      ),
    );
    this.timeWindowTypesSuggestions$ = this.timeWindowTypes$.pipe(
      map((types) =>
        types.filter(({ value }) => value !== OrderTimeWindowType.INTERVAL),
      ),
    );

    const pointOfInterest$ = controls.address.valueChanges.pipe(
      startWith(controls.address.value),
      switchMap((address) => {
        if (!address || address.type !== OnyxAddressType.POINT_OF_INTEREST) {
          return of(null);
        }
        return this.pointOfInterestService.getPointOfInterest(address.poiUuid);
      }),
      shareReplay(1),
      takeUntilDestroyed(this.destroyRef),
    );
    this.pointOfInterest = toSignal(pointOfInterest$, {
      initialValue: null,
      injector: this.injector,
    });

    pointOfInterest$
      .pipe(
        skip(1),
        filter((pointOfInterest) => pointOfInterest != null),
        distinctUntilChanged((previous, { uuid }) => previous.uuid === uuid),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((pointOfInterest) =>
        controls.serviceTime.setValue(pointOfInterest.time.averageServiceTime),
      );

    this.serviceTime = toSignal(controls.serviceTime.valueChanges, {
      initialValue: controls.serviceTime.value,
      injector: this.injector,
    });

    const timeWindowsChanges$ = this.timeWindowsFormArray().valueChanges.pipe(
      startWith(this.timeWindowsFormArray().value),
    );

    this.showAfterHoursWarning = toSignal(
      timeWindowsChanges$.pipe(
        combineLatestWith(pointOfInterest$),
        map(([windows, pointOfInterest]) => {
          if (this.timeWindowsFormArray().invalid) return false;
          if (!pointOfInterest) return false;

          for (const window of windows) {
            if (!window.date) return false;

            const openingHours = chain(window.date)
              .thru((date) => DateTime.fromISO(date).weekday)
              .thru((weekday): OnyxTimeRange | null => {
                const { businessHours } = pointOfInterest;
                if (weekday === 7) return businessHours.sunday;
                if (weekday === 6) return businessHours.saturday;
                return businessHours.weekday;
              })
              .value();

            const type = this.timeWindowType();
            if (type === OrderTimeWindowType.INTERVAL) {
              const timeRange = window.timeRange;
              if (!timeRange) return false;
              if (!openingHours) return timeRange;

              if (
                timeRange.from < openingHours.from ||
                timeRange.to > openingHours.to
              ) {
                return timeRange;
              }
            } else if (type === OrderTimeWindowType.FIX) {
              const time = window.time;
              if (!time) return false;
              if (!openingHours) return time;

              if (time < openingHours.from || time > openingHours.to) {
                return time;
              }
            } else {
              return false;
            }
          }

          return false;
        }),
      ),
      { initialValue: false, injector: this.injector },
    );

    this.showPastDateWarning = toSignal(
      timeWindowsChanges$.pipe(
        map((windows) =>
          windows.every((window) => {
            if (!window.date) return false;

            const endTime =
              this.timeWindowType() === OrderTimeWindowType.FIX
                ? window.time
                : window.timeRange?.to;
            if (!endTime) return false;

            return CommonHelper.isPast(
              DateTime.fromISO(`${window.date}T${endTime}`),
            );
          }),
        ),
      ),
      { initialValue: false, injector: this.injector },
    );

    effect(
      () => {
        if (this.timeWindowType() !== OrderTimeWindowType.FIX) return;
        this.timeWindowsFormArray().controls.splice(1);
      },
      { injector: this.injector },
    );

    effect(
      () => {
        const isCustom = this.addressType() === OnyxAddressType.CUSTOM;
        const isOpeningHours =
          this.timeWindowType() === OrderTimeWindowType.OPENING_HOURS;
        if (!isCustom || !isOpeningHours) return;

        timeWindowTypeControl.reset();
      },
      { injector: this.injector },
    );
  }

  protected addPointOfInterest(name: string | undefined): void {
    const url = this.router
      .createUrlTree([PointsOfInterestRoute.ADD_POINT_OF_INTEREST], {
        queryParams: {
          ...(name && { [PointsOfInterestQueryParamKey.NAME]: name }),
        },
      })
      .toString();
    ActionHelper.openLink(url);
  }

  protected addTimeWindow(): void {
    this.timeWindowsFormArray().push(
      OrderPointTimeWindowFormComponent.buildForm(this.fb),
    );
  }

  protected removeTimeWindow(index: number): void {
    this.orderFormService.addRestorePoint(
      {
        [OrderPointCategory.LOADING]:
          OrderFormRestorePointType.REMOVED_LOADING_TIME_WINDOW,
        [OrderPointCategory.UNLOADING]:
          OrderFormRestorePointType.REMOVED_UNLOADING_TIME_WINDOW,
      }[this.pointCategory()],
    );
    this.timeWindowsFormArray().removeAt(index);
  }

  public static buildTimeWindowsForm(fb: NonNullableFormBuilder, count = 1) {
    return fb.group({
      type: fb.control(OrderTimeWindowType.INTERVAL, [Validators.required]),
      windows: fb.array(
        Array.from({ length: count }).map(() =>
          OrderPointTimeWindowFormComponent.buildForm(fb),
        ),
        [orderPointTimeWindowOverlap],
      ),
    });
  }
}
